import React, { useState } from 'react';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useAppSelector } from '@/redux/app/hooks';
import { isTryDemoViewableRedux } from '@/redux/slices/user-context';
import Header from '../../header';
import Footer from '../../footer';
import StoneityButton from '@/components/stoneity-button/stoneityButton';
import Check from '../../images/check';
import Arrow from '../../images/arrow';
import Link from 'next/link';
import styles from '@/styles/Plan.module.css';

enum PlansEnum {
  Mounth,
  Year,
}

const Plan = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const isTryDemoViewable = useAppSelector(isTryDemoViewableRedux);
  const [plansTab, setPlansTab] = useState(PlansEnum.Mounth);
  const [activeTab, setActiveTab] = useState(null);
  const [activePurchaseTransactionTab, setActivePurchaseTransactionTab] = useState(null);

  const handleTabClick = (index) => {
    setActiveTab((prev) => (prev === index ? null : index));
  };

  const handleTabPurchaseTransactionClick = (index) => {
    setActivePurchaseTransactionTab((prev) => (prev === index ? null : index));
  };

  const frequentlyAskedData = [
    {
      title: t("serviceManagementFrequentlyQuestionTitle1"),
      text: t("serviceManagementFrequentlyQuestionText1")
    },
    {
      title: t("serviceManagementFrequentlyQuestionTitle2"),
      text: t("serviceManagementFrequentlyQuestionText2")
    },
    {
      title: t("serviceManagementFrequentlyQuestionTitle3"),
      text: t("serviceManagementFrequentlyQuestionText3")
    },
    {
      title: t("serviceManagementFrequentlyQuestionTitle4"),
      text: t("serviceManagementFrequentlyQuestionText4")
    },
    {
      title: t("serviceManagementFrequentlyQuestionTitle5"),
      text: t("serviceManagementFrequentlyQuestionText5")
    },
    {
      title: t("serviceManagementFrequentlyQuestionTitle6"),
      text: t("serviceManagementFrequentlyQuestionText6")
    },
  ];

  const purchaseTransaction = [
    {
      title: t("purchaseTransactionFrequentlyQuestionTitle1"),
      text: t("purchaseTransactionFrequentlyQuestionText1")
    },
    {
      title: t("purchaseTransactionFrequentlyQuestionTitle2"),
      text: t("purchaseTransactionFrequentlyQuestionText2")
    },
    {
      title: t("purchaseTransactionFrequentlyQuestionTitle3"),
      text: t("purchaseTransactionFrequentlyQuestionText3")
    },
    {
      title: t("purchaseTransactionFrequentlyQuestionTitle4"),
      text: t("purchaseTransactionFrequentlyQuestionText4")
    },
    {
      title: t("purchaseTransactionFrequentlyQuestionTitle5"),
      text: t("purchaseTransactionFrequentlyQuestionText5")
    },
  ];

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = `/service-management/plan`;

  return (
    <>
      <Head>
        <title>{t('seo_service_management_plan_title')}</title>
        <meta name="description" content={t('seo_service_management_plan_description')} />
        <meta name="keywords" content="service management planları, fiyatlandırma, ITSM planları, servis yönetimi fiyatları, Stoneity, pricing, ITSM plans, service management pricing" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_service_management_plan_title')} />
        <meta property="og:description" content={t('seo_service_management_plan_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_service_management_plan_title')} />
        <meta name="twitter:description" content={t('seo_service_management_plan_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <div className={styles.mainFlex}>
        <div>
          <div className={styles.pageFlex}>
            <div className={styles.planingBannerWrapper}>
              <div className={styles.title}>{t('stoneityServiceManagementPlan')}</div>
              <div className={styles.text}>{t('yourCompleteSolutionForTicketing')}</div>
              {/* <div className={styles.buttonWrapper}>
                <StoneityButton
                  buttonClass='planingBannerButton'
                  buttonText='Aylık'
                  buttonStyle='light'
                  width={240}
                  height={50}
                  onClick={() => { setPlansTab(PlansEnum.Mounth) }}
                  isSelected={plansTab === PlansEnum.Mounth}
                />
                <StoneityButton
                  buttonClass='planingBannerButton'
                  buttonText='Yıllık'
                  buttonStyle='light'
                  width={240}
                  height={50}
                  onClick={() => { setPlansTab(PlansEnum.Year) }}
                  isSelected={plansTab === PlansEnum.Year}
                />
              </div> */}
            </div>
          </div>
          <div className={styles.pageFlex} style={{ position: 'relative' }}>
            <div className={styles.planinCardBackground}></div>
            <div className={styles.planingCardWrapper}>
              <div className={styles.planingCard}>
                <div className={styles.title}>{t('freePlan')}</div>
                {isTryDemoViewable &&
                  <div className={styles.height}></div>
                }
                {/* <div className={styles.text}>Görevlerini yönetmek isteyen bireyler ve küçük ekipler için.</div> */}
                {/* <div className={styles.price}>US$0</div> */}
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle='light'
                      buttonText={t("tryItForFree")}
                    />
                  </Link>
                }
                {/* <div className={styles.planinCardFeaturesListTitle}>Manage tasks and personal to-dos:</div> */}
                <ul className={styles.planinCardFeaturesList}>
                  <li><Check />{t('onePortal')}</li>
                  <li><Check />{t('oneService')}</li>
                  <li><Check />{t('user100')}</li>
                  <li><Check />{t('recordLimit1000')}</li>
                  <li><Check />{t('user10')}</li>
                  <li><Check />{t('limetedRole')}</li>
                  <li><Check />{t('approvalProcesses')}</li>
                  <li><Check />{t('workflow')}</li>
                  <li><Check />{t('2GBStorage')}</li>
                  <li><Check />{t('dynamicForm')}</li>
                  <li><Check />{t('taskAssignment')}</li>
                  <li><Check />{t('taskDeadline')}</li>
                  <li><Check />{t('dynamicIssue')}</li>
                  <li><Check />{t('liveSystem')}</li>
                  <li><Check />{t('dynamicMail')}</li>
                  <li><Check />{t('allView')}</li>
                </ul>
              </div>
              <div className={styles.planingCard}>
                <div className={styles.mostChosen}>{t('mostSelected')}</div>
                <div className={styles.title}>{t('superiorPlan')}</div>
                {/* <div className={styles.text}>Görevlerini yönetmek isteyen bireyler ve küçük ekipler için.</div> */}
                <div className={styles.text} style={{ margin: "0 0 20px 0" }}>{t("350usdmonth")}</div>
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle='dark'
                      buttonText={t("start")}
                    />
                  </Link>
                }
                {/* <div className={styles.planinCardFeaturesListTitle}>Manage tasks and personal to-dos:</div> */}
                <ul className={styles.planinCardFeaturesList}>
                  <li><Check />{t('allFeaturesOfTheFreePlan')}</li>
                  <li><Check />{t('onePortal')}</li>
                  <li><Check />{t('unlimitedService')}</li>
                  <li><Check />{t('user2000')}</li>
                  <li><Check />{t('flexibleRole')}</li>
                  <li><Check />{t('unlimitedDynamicForm')}</li>
                  <li><Check />{t('unlimitedKnowledBase')}</li>
                  <li><Check />{t('5GBStorage')}</li>
                  <li><Check />{t('mailEntegration')}</li>
                  <li><Check />{t('upgradeAutomation')}</li>
                  <li><Check />{t('slaMethod')}</li>
                  <li><Check />{t('customerMessageModule')}</li>
                  <li><Check />{t('performanceAndCapacity')}</li>
                </ul>
              </div>
              <div className={styles.planingCard}>
                <div className={styles.title}>{t('enterprisePlan')}</div>
                {/* <div className={styles.text}>Görevlerini yönetmek isteyen bireyler ve küçük ekipler için.</div> */}
                {isTryDemoViewable &&
                  <div className={styles.height}></div>
                }
                {/* <div className={styles.price}>{plansTab === PlansEnum.Mounth ? 'US$100' : 'US$1000'}</div> */}
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle='light'
                      buttonText={t("start")}
                    />
                  </Link>
                }
                {/* <div className={styles.planinCardFeaturesListTitle}>Manage tasks and personal to-dos:</div> */}
                <ul className={styles.planinCardFeaturesList}>
                  <li><Check />{t('allFeaturesOfTheSuperiorPlan')}</li>
                  <li><Check />{t('unlimitedPortal')}</li>
                  <li><Check />{t('unlimitedUser')}</li>
                  <li><Check />{t('unlimitedStorage')}</li>
                  <li><Check />{t('advancedReporting')}</li>
                  <li><Check />{t('auditLog')}</li>
                  <li><Check />{t('onPremiseUsage')}</li>
                  <li><Check />{t('ldap')}</li>
                  <li><Check />{t('activeDirectory')}</li>
                  <li><Check />{t('multipleBrands')}</li>
                  <li><Check />{t('upgradeSla')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.frequentlyAskedWrapper}>
            <div className={styles.title}>{t("frequentlyAskedQuestion")}</div>
            {frequentlyAskedData.map((tab, index) => (
              <div
                key={index}
                className={styles.frequentlyAskedTab}
                onClick={() => handleTabClick(index)}
              >
                <div className={styles.frequentlyAskedTabTitleWrapper}>
                  {tab.title}
                  <Arrow rotate={activeTab === index ? "180deg" : ""} />
                </div>
                <div
                  className={`${styles.frequentlyAskedTabTextWrapper} ${activeTab === index ? styles.active : ""
                    }`}
                >
                  <div className={styles.frequentlyAskedTabText}>
                    {tab.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.frequentlyAskedWrapper}>
            <div className={styles.title}>{t("purchaseTransaction")}</div>
            {purchaseTransaction.map((tab, index) => (
              <div
                key={index}
                className={styles.frequentlyAskedTab}
                onClick={() => handleTabPurchaseTransactionClick(index)}
              >
                <div className={styles.frequentlyAskedTabTitleWrapper}>
                  {tab.title}
                  <Arrow rotate={activePurchaseTransactionTab === index ? "180deg" : ""} />
                </div>
                <div
                  className={`${styles.frequentlyAskedTabTextWrapper} ${activePurchaseTransactionTab === index ? styles.active : ""
                    }`}
                >
                  <div className={styles.frequentlyAskedTabText}>
                    {tab.text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || 'en', ['common'])),
  },
});

export default Plan;